import React, { useState } from "react";
import { Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { api, formatError } from "../lib/api";

const INPUT = "w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-ronil-navy/30 focus:border-ronil-navy transition";
const LABEL = "block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-heading font-semibold mb-1.5";

function validate(current, next, confirm) {
  if (next.length < 8) return "A nova palavra-passe deve ter pelo menos 8 caracteres.";
  if (!/[A-Za-z]/.test(next) || !/\d/.test(next)) return "A nova palavra-passe deve conter letras e números.";
  if (next === current) return "A nova palavra-passe deve ser diferente da actual.";
  if (next !== confirm) return "As duas palavras-passe não coincidem.";
  return "";
}

export const ChangePasswordForm = ({ onSuccess, submitLabel = "Alterar palavra-passe" }) => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const msg = validate(current, next, confirm);
    setError(msg);
    if (msg) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setConfirm("");
      onSuccess?.(data);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail, "Não foi possível alterar a palavra-passe."));
    } finally {
      setSubmitting(false);
    }
  };

  const type = show ? "text" : "password";
  return (
    <form onSubmit={submit} className="space-y-4 max-w-md" data-testid="change-password-form">
      <div>
        <label className={LABEL}>Palavra-passe actual</label>
        <div className="relative">
          <input data-testid="change-password-current" type={type} value={current} onChange={(e) => setCurrent(e.target.value)} required autoComplete="current-password" className={`${INPUT} pr-10`} />
          <button type="button" data-testid="change-password-toggle" onClick={() => setShow((v) => !v)} aria-label={show ? "Esconder" : "Mostrar"} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-ronil-navy p-1 transition-colors">
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <label className={LABEL}>Nova palavra-passe</label>
        <input data-testid="change-password-new" type={type} value={next} onChange={(e) => setNext(e.target.value)} required autoComplete="new-password" className={INPUT} />
        <p className="text-[11px] text-slate-500 mt-1">Mínimo 8 caracteres, com letras e números.</p>
      </div>
      <div>
        <label className={LABEL}>Confirmar nova palavra-passe</label>
        <input data-testid="change-password-confirm" type={type} value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="new-password" className={INPUT} />
      </div>
      {error && (
        <div data-testid="change-password-error" className="flex items-start gap-2 px-3 py-2 bg-red-50 text-red-800 border border-red-200 rounded-md text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /><span>{error}</span>
        </div>
      )}
      <button data-testid="change-password-submit" type="submit" disabled={submitting} className="w-full bg-ronil-navy hover:bg-ronil-navy-hover text-white rounded-md py-2.5 text-sm font-heading font-semibold tracking-wide transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {submitLabel}
      </button>
    </form>
  );
};
